import type { StreamStatus } from "../../lib/api";
import type { PlayerPresentation } from "./playerCopy";
import type { PlayerState } from "./playerState";

/**
 * What the SERVER says about a camera, in operator words — a different
 * question from PlayerState, which is what THIS BROWSER's player is doing.
 * The two disagree routinely: a camera can be pulled fine for three other
 * viewers while this browser's own tile is idle, and a tile can keep playing
 * out of buffer for several seconds after the server has actually lost the
 * RTSP session. Today the tile's header StatusDot answers only the local
 * question — this module exists to answer the other one without merging the
 * two into one indicator that would answer neither correctly (see
 * VideoTile.tsx's own comment where this is consumed).
 */
export type ServerHealthKind = "unknown" | "idle" | "starting" | "ready" | "failed";

export interface ServerHealth {
  kind: ServerHealthKind;
  /** True only for a state worth spending pixels on, on a wall where most
   *  tiles are fine most of the time. */
  isNoteworthy: boolean;
  /** Short, always-safe-to-render label. */
  label: string;
  /** One sentence, curated — never StreamStatus.last_error verbatim. That
   *  field is a raw Go error string; the backend redacts credentials out of
   *  it (see fix/redact-stream-last-error) but that makes it safe to leave
   *  the server, not readable as operator copy. */
  detail?: string;
  viewerCount: number;
  bitrateKbps: number;
}

const UNKNOWN: ServerHealth = { kind: "unknown", isNoteworthy: false, label: "", viewerCount: 0, bitrateKbps: 0 };

/**
 * @param status The camera's entry in the health feed's snapshot, if one has
 *   arrived.
 * @param isFeedLive Whether the health feed itself is currently live
 *   (`health.phase === "live"`, from useHealthEvents). A snapshot held from
 *   before an interruption is the last thing that was true, not necessarily
 *   the thing that's true now — asserting it as current would be worse than
 *   saying nothing, on a surface whose only job is being trustworthy at a
 *   glance.
 */
export function describeServerHealth(status: StreamStatus | undefined, isFeedLive: boolean): ServerHealth {
  if (!isFeedLive || !status) return UNKNOWN;

  switch (status.state) {
    case "idle":
      return { kind: "idle", isNoteworthy: false, label: "Not running", viewerCount: 0, bitrateKbps: 0 };
    // Collapsed into one kind: both are "coming up for somebody", and
    // neither is a fact a viewer needs to distinguish from the other.
    case "connecting":
    case "active":
      return {
        kind: "starting",
        isNoteworthy: false,
        label: "Connecting…",
        viewerCount: status.viewer_count,
        bitrateKbps: status.bitrate_kbps,
      };
    case "ready":
      return {
        kind: "ready",
        isNoteworthy: false,
        label: "Live",
        viewerCount: status.viewer_count,
        bitrateKbps: status.bitrate_kbps,
      };
    case "failed":
      return {
        kind: "failed",
        isNoteworthy: true,
        label: "No signal",
        detail: describeFailure(status),
        viewerCount: status.viewer_count,
        bitrateKbps: status.bitrate_kbps,
      };
  }
}

function describeFailure(status: StreamStatus): string {
  const base = "The server lost this camera's stream and is retrying.";
  // reconnect_count is cumulative for the life of the pull and never resets
  // (see the streaming engine), so it is not "how many times in a row this
  // has failed just now" — a camera that flapped once hours ago carries the
  // same number for the rest of its life. Worth naming, not worth reading as
  // urgency.
  if (status.reconnect_count > 0) {
    return `${base} Retried ${status.reconnect_count} time${status.reconnect_count === 1 ? "" : "s"} since it started.`;
  }
  return base;
}

/** The one definition of "the server is currently pulling this camera" —
 *  shared so "live" means the same thing everywhere it's asked, rather than
 *  four call sites each independently re-deriving `state === "ready"`. */
export function isServerLive(status: StreamStatus | undefined): boolean {
  return status?.state === "ready";
}

/**
 * Folds server health into a tile's own presentation — but only while the
 * LOCAL player is idle. A playing or erroring tile already has four other
 * ways of stating its own condition (the picture itself, the header dot, the
 * LIVE/PAUSED token, which transport buttons are offered); idle is the one
 * state that currently says nothing beyond "not watching", which is exactly
 * where "somebody else already is" or "the camera itself is unreachable"
 * goes unstated today.
 */
export function applyServerHealth(
  presentation: PlayerPresentation,
  playerState: PlayerState,
  serverHealth: ServerHealth,
): PlayerPresentation {
  if (playerState.kind !== "idle") return presentation;

  switch (serverHealth.kind) {
    case "failed":
      return { ...presentation, title: "Camera has no signal", hint: serverHealth.detail, tone: "danger" };
    // Joining costs nothing — the server shares one RTSP pull across every
    // viewer (see FocusDialog's own autoStart reasoning for the same fact) —
    // so an idle tile next to an already-running one is worth saying so.
    case "ready":
      return {
        ...presentation,
        hint: `Already streaming for ${serverHealth.viewerCount} ${serverHealth.viewerCount === 1 ? "viewer" : "viewers"} — press play to join.`,
      };
    case "starting":
      return { ...presentation, hint: "Coming up for someone else…" };
    default:
      return presentation;
  }
}
